
import React, { useEffect, useState } from "react";
import toast from "react-hot-toast";
import api from "../../../api/Api.js";
import { Link, useNavigate } from "react-router-dom";
import { ArrowLeft } from "lucide-react";
import Swal from "sweetalert2";

const AddStatePage = () => {
  // =========================
  // NAVIGATE
  // =========================
  const navigate = useNavigate();

  // =========================
  // STATE ROWS
  // =========================
  const [stateRows, setStateRows] = useState([
    {
      cntry_id: "",
      state: "",
      slug: "",
    },
  ]);

  const [Countrys, setCountrys] = useState([]);
  const [loadingStates, setLoadingStates] = useState(false);
  const [saving, setSaving] = useState(false);

  // =========================
  // FETCH COUNTRY DATA
  // =========================
  const fetchCountryData = async () => {
    setLoadingStates(true);

    try {
      const res = await api.get(
        "/adminHolidays/get-holidays-country-data"
      );

      const countryData = res.data?.data;

      setCountrys(
        Array.isArray(countryData) ? countryData : []
      );
    } catch (error) {
      console.error("Fetch Country Error:", error);
      toast.error("Failed to load Country data");
      setCountrys([]);
    } finally {
      setLoadingStates(false);
    }
  };

  useEffect(() => {
    fetchCountryData();
  }, []);

  // =========================
  // ADD ROW
  // =========================
  const addStateRow = () => {
    setStateRows((prevRows) => [
      ...prevRows,
      {
        cntry_id: "",
        state: "",
        slug: "",
      },
    ]);
  };

  // =========================
  // REMOVE ROW
  // =========================
  const removeStateRow = async (index) => {
    if (stateRows.length === 1) {
      toast.error("At least one row is required");
      return;
    }

    const confirm = await Swal.fire({
      title: "Delete Row?",
      text: "Are you sure you want to remove this row?",
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "Yes, Delete",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#dc3545",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    setStateRows((prevRows) =>
      prevRows.filter((_, i) => i !== index)
    );
  };

  // =========================
  // GENERATE SLUG
  // =========================
  const generateSlug = (value) => {
    return String(value || "")
      .toLowerCase()
      .trim()
      .replace(/[^\w\s-]/g, "")
      .replace(/\s+/g, "-")
      .replace(/-+/g, "-");
  };

  // =========================
  // HANDLE INPUT CHANGE
  // =========================
  const handleStateChange = (index, field, value) => {
    setStateRows((prevRows) => {
      const updatedRows = [...prevRows];

      updatedRows[index] = {
        ...updatedRows[index],
        [field]: value,
      };

      // Automatically generate slug when state name changes
      if (field === "state") {
        updatedRows[index].slug = generateSlug(value);
      }

      return updatedRows;
    });
  };

  // =========================
  // VALIDATE STATE ROWS
  // =========================
  const validateStateRows = () => {
    const hasEmptyCountry = stateRows.some(
      (row) => !String(row.cntry_id || "").trim()
    );

    if (hasEmptyCountry) {
      toast.error("Please select Country Name in every row");
      return false;
    }

    const hasEmptyState = stateRows.some(
      (row) => !String(row.state || "").trim()
    );

    if (hasEmptyState) {
      toast.error("State Name is required");
      return false;
    }

    const hasEmptySlug = stateRows.some(
      (row) => !String(row.slug || "").trim()
    );

    if (hasEmptySlug) {
      toast.error("Slug is required");
      return false;
    }

    return true;
  };

  // =========================
  // SAVE STATE
  // =========================
  const saveState = async () => {
    if (saving) return;

    if (!validateStateRows()) {
      return;
    }

    const confirm = await Swal.fire({
      title: "Save State Name?",
      text: "Do you want to save these State Names?",
      icon: "question",
      showCancelButton: true,
      confirmButtonText: "Yes, Save",
      cancelButtonText: "Cancel",
      confirmButtonColor: "#0d6efd",
    });

    if (!confirm.isConfirmed) {
      return;
    }

    setSaving(true);

    try {
      // Send country ID along with state name and slug
      const payload = {
        states: stateRows.map((row) => ({
          cntry_id: row.cntry_id,
          state: row.state.trim(),
          slug: row.slug.trim(),
        })),
      };

      console.log("STATE PAYLOAD =>", payload);

      const res = await api.post(
        "/adminHolidays/holidays-state-save",
        payload
      );

      console.log("STATE SAVE RESPONSE =>", res?.data);

      if (res?.data?.success) {
        await Swal.fire({
          title: "Success",
          text: "State Name Saved Successfully",
          icon: "success",
          confirmButtonText: "OK",
        });

        // Reset with the correct field names
        setStateRows([
          {
            cntry_id: "",
            state: "",
            slug: "",
          },
        ]);

        navigate("/holidays/state/manage-state");
      } else {
        await Swal.fire({
          title: "Error",
          text:
            res?.data?.message ||
            "Unable to save State Name",
          icon: "error",
          confirmButtonText: "OK",
        });
      }
    } catch (error) {
      console.error("Save State Error =>", error);

      const errorMessage =
        error?.response?.data?.message ||
        error?.message ||
        "Server Error";

      toast.error(errorMessage);
    } finally {
      setSaving(false);
    }
  };

  // =========================
  // JSX
  // =========================
  return (
    <div className="container-fluid py-3 bg-white min-vh-100">

      {/* HEADER */}
      <div className="card shadow-sm border-0 mb-4 bg-primary-subtle rounded-3">
        <div className="card-body d-flex justify-content-between align-items-center flex-wrap gap-2">
          <h4 className="fw-bold mb-0">
            Add State Name
          </h4>

          <Link
            to="/holidays/state/manage-state"
            className="btn btn-outline-secondary btn-sm d-flex align-items-center gap-2"
          >
            <ArrowLeft size={16} />
            Back
          </Link>
        </div>
      </div>

      {/* STATE TABLE */}
      <div className="row">
        <div className="col-lg-12">
          <div className="card shadow-sm border-0">
            <div className="card-body">
              <div className="table-responsive">

                <table className="table table-hover table-bordered align-middle mb-0">

                  <thead className="table-light">
                    <tr>
                      <th
                        width="70"
                        className="text-center"
                      >
                        Sl.No
                      </th>

                      <th>Country Name</th>
                      <th>State Name</th>
                      <th>Slug</th>

                      <th
                        width="140"
                        className="text-center"
                      >
                        Action
                      </th>
                    </tr>
                  </thead>

                  <tbody>
                    {stateRows.map((row, index) => (
                      <tr key={index}>

                        {/* SERIAL NUMBER */}
                        <td className="text-center fw-semibold">
                          {index + 1}
                        </td>

                        {/* COUNTRY NAME */}
                        <td>
                          <select
                            className="form-select"
                            value={row.cntry_id}
                            disabled={loadingStates || saving}
                            onChange={(e) =>
                              handleStateChange(
                                index,
                                "cntry_id",
                                e.target.value
                              )
                            }
                          >
                            <option value="">
                              {loadingStates
                                ? "Loading Country..."
                                : "- Select Country -"}
                            </option>

                            {Countrys.map((cat) => (
                              <option
                                key={cat.cntry_id}
                                value={cat.cntry_id}
                              >
                                {cat.name}
                              </option>
                            ))}
                          </select>
                        </td>

                        {/* STATE NAME */}
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter State Name"
                            value={row.state}
                            disabled={saving}
                            onChange={(e) =>
                              handleStateChange(
                                index,
                                "state",
                                e.target.value
                              )
                            }
                          />
                        </td>

                        {/* SLUG */}
                        <td>
                          <input
                            type="text"
                            className="form-control"
                            placeholder="Enter Slug"
                            value={row.slug}
                            disabled={saving}
                            onChange={(e) =>
                              handleStateChange(
                                index,
                                "slug",
                                e.target.value
                              )
                            }
                          />
                        </td>

                        {/* ACTION */}
                        <td className="text-center">

                          {/* ADD ROW */}
                          <button
                            type="button"
                            className="btn btn-success btn-sm me-2"
                            onClick={addStateRow}
                            disabled={saving}
                            title="Add Row"
                          >
                            <i className="fa fa-plus-square"></i>
                          </button>

                          {/* DELETE ROW */}
                          <button
                            type="button"
                            className="btn btn-danger btn-sm"
                            onClick={() => removeStateRow(index)}
                            disabled={saving}
                            title="Delete Row"
                          >
                            <i className="fa fa-trash"></i>
                          </button>

                        </td>
                      </tr>
                    ))}
                  </tbody>

                </table>
              </div>
            </div>
          </div>
        </div>

        {/* SAVE BUTTON */}
        <div className="col-lg-12 text-center">
          <button
            type="button"
            className="btn btn-info text-white px-5 mt-3"
            onClick={saveState}
            disabled={saving || loadingStates}
          >
            {saving ? (
              <>
                <span
                  className="spinner-border spinner-border-sm me-2"
                  role="status"
                  aria-hidden="true"
                ></span>
                Saving...
              </>
            ) : (
              <>
                <i className="fa fa-save me-2"></i>
                Save State Name
              </>
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default AddStatePage;
